import localforage from 'localforage';

/**
 * Shared localforage instance for task session data
 */
export const tasksStore = localforage.createInstance({ name: 'Tasks' });

/**
 * Storage keys for session tracking
 */
export const TASK_STORAGE_KEYS = {
  COMPLETED_INFOV: 'completedInfovTasks',
  COMPLETED_DISTRIBUTION: 'completedDistributionTasks',
  DISTRIBUTION_RESULTS: 'distributionResults',
};

/**
 * Task type codes that require modal for completion
 */
export const TASK_TYPES_REQUIRING_MODAL = [
  'KOMENTAR',
  'FOTO',
  'FOTOKOM',
  'POZICIJE',
  'INFO',
  'INFOV',
];

/**
 * Task type codes that are repeatable (can be executed multiple times)
 */
export const REPEATABLE_TASK_TYPE_CODES = ['INFOV'];

/**
 * Task category codes that are repeatable
 */
export const REPEATABLE_TASK_CATEGORY_CODES = ['DIS'];

/**
 * Checks if a task type requires modal for completion
 */
export const requiresModalForCompletion = (taskTypeCode) => {
  return TASK_TYPES_REQUIRING_MODAL.includes(taskTypeCode);
};

/**
 * Checks if a task is repeatable based on type or category
 */
export const isRepeatableTask = (task) => {
  return (
    REPEATABLE_TASK_TYPE_CODES.includes(task?.type?.code) ||
    REPEATABLE_TASK_CATEGORY_CODES.includes(task?.categoryCode)
  );
};

/**
 * Checks if task is INFOV type (repeatable info task with modal)
 * Use this for INFOV-specific UI logic (StatusButtons, execution count display)
 */
export const isInfovTask = (task) => {
  return task?.type?.code === 'INFOV';
};

/**
 * Generic helper to check if task was completed this session
 */
export const getCompletedTasksForSession = async (storageKey) => {
  try {
    return (await tasksStore.getItem(storageKey)) || {};
  } catch (err) {
    console.error(`Error loading ${storageKey}:`, err);
    return {};
  }
};

/**
 * Generic helper to mark task as completed for this session
 */
export const markTaskCompletedForSession = async (storageKey, taskId) => {
  try {
    const completed = await getCompletedTasksForSession(storageKey);
    completed[taskId] = true;
    await tasksStore.setItem(storageKey, completed);
    return true;
  } catch (err) {
    console.error(`Error saving to ${storageKey}:`, err);
    return false;
  }
};

/**
 * Clears completed INFOV tasks session tracking
 */
export const clearCompletedInfovTasks = async () => {
  try {
    await tasksStore.removeItem(TASK_STORAGE_KEYS.COMPLETED_INFOV);
  } catch (err) {
    console.error('Error clearing completed INFOV tasks:', err);
  }
};

/**
 * Clears completed distribution tasks session tracking
 */
export const clearCompletedDistributionTasks = async () => {
  try {
    await tasksStore.removeItem(TASK_STORAGE_KEYS.COMPLETED_DISTRIBUTION);
  } catch (err) {
    console.error('Error clearing completed distribution tasks:', err);
  }
};

/**
 * Clears all completed task session tracking (call on customer exit)
 */
export const clearAllTaskSessionTracking = async () => {
  await Promise.all([
    clearCompletedInfovTasks(),
    clearCompletedDistributionTasks(),
  ]);
};

/**
 * Order-based task category codes that are auto-completed via orders
 */
export const ORDER_BASED_TASK_CODES = ['ULI', 'PPR', 'CIGPPR', 'CIGULI'];

/**
 * Distribution task category code
 */
export const DISTRIBUTION_TASK_CODE = 'DIS';

/**
 * Checks if a task is order-based (auto-completed via orders)
 */
export const isOrderBasedTask = (task) => {
  return (
    ORDER_BASED_TASK_CODES.includes(task?.type?.code) ||
    ORDER_BASED_TASK_CODES.includes(task?.categoryCode) ||
    task?.raster === 'D' ||
    task?.ulistan === 'D'
  );
};

/**
 * Checks if a task is a distribution task
 */
export const isDistributionTask = (task) => {
  return task?.categoryCode === DISTRIBUTION_TASK_CODE;
};

/**
 * Enriches task data with status information from nodes
 * Optimized with Map for O(n+m) instead of O(n*m)
 */
export const enrichTasksWithStatus = (tasks, nodesData) => {
  if (!tasks || !Array.isArray(tasks)) return [];
  if (!nodesData || !Array.isArray(nodesData)) return tasks;

  const nodesMap = new Map(
    nodesData.map((node) => [String(node.id), node])
  );

  return tasks.map((task) => {
    const taskNode = nodesMap.get(String(task.id));
    return {
      ...task,
      status: taskNode?.status || null,
      isCompleted: taskNode?.status?.final === 'D',
    };
  });
};

/**
 * Filters tasks by customer ID
 */
export const filterTasksByCustomer = (tasks, customerId) => {
  if (!customerId || !Array.isArray(tasks)) return tasks || [];
  const customerIdStr = String(customerId);
  return tasks.filter((task) => String(task.customer?.id) === customerIdStr);
};

/**
 * Order type codes for tobacco products
 */
export const TOBACCO_ORDER_CODES = ['11', '13', '16'];

/**
 * Filters products by order type (tobacco/POS)
 * Optimized to single filter pass
 */
export const filterProductsByOrderType = (products, orderType) => {
  if (!products?.length || !orderType) return [];

  const isPosOrder = orderType.code === '20';
  const isTobaccoOrder = TOBACCO_ORDER_CODES.includes(orderType.code);

  return products.filter((item) => {
    const posMatch = isPosOrder ? item.isPos : !item.isPos;
    const tobaccoMatch = isTobaccoOrder ? item.isTobacco : !item.isTobacco;
    return posMatch && tobaccoMatch;
  });
};
